import { MultiSelectOption } from '@/components/ui/multi-select.js';
import {
  buildCoachSelectOptions,
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
} from '@/components/ui/multi-select-utils.js';
import {
  EmptyArray,
  getCoach,
  getCoaches,
  getLearningPaths,
  getLesson,
  getLessonsForCoach,
  getLessonsTypeahead,
  getMinimalOrgs,
  getMinimalSchools,
  getSchedules,
  getUsersTypeahead,
  listPayCycles,
} from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { FrequentTimeZones, student } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import {
  adjustMinimalClassTimeAcrossTZ,
  DEPRECATED_allPagesModifier,
  ClassTimeType,
  GetCoachesSortOrder,
  FullCoach,
  GetCoachesModifier,
  GetLessonsTypeaheadModifier,
  GetMinimalOrgsResults,
  GetPayCyclesResult,
  GetUsersTypeaheadFilter,
  GetUsersTypeaheadModifier,
  MinimalSchoolWithOrg,
  PagingModifier,
  typeaheadPagesModifier,
  UserLike,
} from '@airreading/common/data';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { CoachesTypeaheadSearchOptions, sortAvailableCoachesByPriority } from './coach-utils.js';

export function useAllSchedules({ schoolId }: { schoolId?: string } = {}) {
  const {
    data: allSchedulesResponse,
    isPending,
    error,
  } = useQuery(getSchedules({ ...DEPRECATED_allPagesModifier, schoolId }));
  const allSchedules = useMemo(() => allSchedulesResponse?.schedules, [allSchedulesResponse]);
  return { data: allSchedules, isPending, error };
}

export function useClassTimeOptions({
  schoolId,
  classTimeTypes = ['recurring'], // by default, we only show recurring class times since one-time class times are not user-facing
}: {
  schoolId: string;
  classTimeTypes?: ClassTimeType[];
}) {
  const schedulesResp = useAllSchedules({ schoolId });
  const schedules = schedulesResp.data || EmptyArray;
  const classTimes = useMemo(
    () =>
      schedules
        .flatMap(({ classTimes, ...otherSchedule }) =>
          classTimes.map((classTime) => ({
            classTime,
            schedule: otherSchedule,
          })),
        )
        .filter(({ classTime: { type } }) => {
          return classTimeTypes.length === 0 || classTimeTypes.includes(type);
        })
        .toSorted((a, b) => {
          const scheduleCompare = a.schedule.name.localeCompare(b.schedule.name);

          if (scheduleCompare !== 0) {
            return scheduleCompare;
          }

          const aTime = a.classTime.meetingBlocks.toSorted((x, y) =>
            Temporal.PlainTime.compare(x.startPlainTime, y.startPlainTime),
          )[0]?.startPlainTime;
          const bTime = b.classTime.meetingBlocks.toSorted((x, y) =>
            Temporal.PlainTime.compare(x.startPlainTime, y.startPlainTime),
          )[0]?.startPlainTime;

          if (!aTime || !bTime) {
            return 0;
          }

          return Temporal.PlainTime.compare(aTime, bTime);
        }),
    [classTimeTypes, schedules],
  );

  const classTimeOptions: MultiSelectOption[] = useMemo(() => {
    return classTimes.map(
      ({
        classTime,
        schedule: {
          name: scheduleName,
          school: {
            org: { timeZone },
          },
        },
      }) => ({
        label: `${scheduleName}: ${formatStudentClassTimeAbbr(adjustMinimalClassTimeAcrossTZ(classTime, timeZone, FrequentTimeZones.UsCentral))}`,
        value: classTime.id,
      }),
    );
  }, [classTimes]);

  const classTimeWithSchoolOptions: MultiSelectOption[] = useMemo(() => {
    return classTimes.map(({ classTime, schedule: { name: scheduleName, school } }) => ({
      label: `${school.name} ${scheduleName}: ${formatStudentClassTimeAbbr(adjustMinimalClassTimeAcrossTZ(classTime, school.org.timeZone, FrequentTimeZones.UsCentral))}`,
      value: classTime.id,
    }));
  }, [classTimes]);

  return {
    ...schedulesResp,
    classTimes,
    classTimeOptions,
    classTimeWithSchoolOptions,
  };
}

export function useAllLearningPaths() {
  const resp = useQuery(getLearningPaths());

  return {
    ...resp,
    learningPaths: resp.data || EmptyArray,
  };
}

export function useAllSchools(): {
  data: MinimalSchoolWithOrg[];
  isPending: boolean;
} {
  // Use the minimal schools endpoint instead of the full schools endpoint
  const { data: allSchoolsResponse, isPending } = useQuery(getMinimalSchools(DEPRECATED_allPagesModifier));
  const allSchools = useMemo(() => allSchoolsResponse?.schools || EmptyArray, [allSchoolsResponse]);
  return { data: allSchools, isPending };
}

export function useSchoolOptions(): MultiSelectOption[] {
  const { data, isPending } = useAllSchools();
  return useMemo(
    () => (isPending ? [] : data?.map((school) => ({ label: school.name, value: school.id })) ?? []),
    [data, isPending],
  );
}

export function useSchoolAndOrgOptions({ orgIds }: { orgIds?: string[] } = {}): MultiSelectOption[] {
  const { data, isPending } = useAllSchools();

  const filteredSchools = useMemo(() => {
    if (!orgIds || orgIds.length === 0) return data;
    return data.filter((school) => orgIds.includes(school.orgId));
  }, [data, orgIds]);

  return useMemo(
    () => (isPending ? [] : filteredSchools?.map((school) => ({ label: school.name, value: school.id })) ?? []),
    [filteredSchools, isPending],
  );
}

export function useAllMinimalOrgs(): {
  data: GetMinimalOrgsResults['orgs'];
  isPending: boolean;
} {
  const { data: allOrgsResponse, isPending } = useQuery(getMinimalOrgs(DEPRECATED_allPagesModifier));
  const allOrgs = useMemo(() => allOrgsResponse?.orgs || EmptyArray, [allOrgsResponse]);
  return { data: allOrgs, isPending };
}

export function useOrgOptions(): MultiSelectOption[] {
  const { data, isPending } = useAllMinimalOrgs();
  return useMemo(
    () => (isPending ? [] : data?.map((org) => ({ label: org.name, value: org.id })) ?? []),
    [data, isPending],
  );
}

export const useAllLessonsForCoach = () => {
  const resp = useQuery({ ...getLessonsForCoach(DEPRECATED_allPagesModifier), staleTime: 30000 });
  return {
    ...resp,
    lessons: resp.data?.lessons || EmptyArray,
  };
};

export const lessonTypeaheadSearch = (queryClient: QueryClient) => {
  return async (typeahead: string) => {
    const req: GetLessonsTypeaheadModifier = {
      ...typeaheadPagesModifier,
      filters: {
        typeahead,
      },
    };

    return queryClient
      .fetchQuery({
        ...getLessonsTypeahead(req),
        staleTime: 30000,
        queryKey: ['LESSONS_TYPEAHEAD_OPTIONS', req],
      })
      .then((results) => {
        return results.map(buildNameLikeSelectOptions);
      });
  };
};

export const userTypeaheadSearch = (
  queryClient: QueryClient,
  filtersWithoutTypeahead?: Omit<GetUsersTypeaheadFilter, 'typeahead'>,
) => {
  return async (typeahead: string): Promise<MultiSelectOption[]> => {
    const modifier = {
      ...typeaheadPagesModifier,
      filters: {
        typeahead,
        ...filtersWithoutTypeahead,
      },
    } satisfies GetUsersTypeaheadModifier;

    return queryClient
      .fetchQuery({
        ...getUsersTypeahead(modifier),
        staleTime: 30000,
        queryKey: ['users', modifier],
      })
      .then((results) => {
        return results.map(buildUserSelectOptions);
      });
  };
};

export const coachesTypeaheadSearch = (queryClient: QueryClient, options?: CoachesTypeaheadSearchOptions) => {
  return async (typeahead: string): Promise<MultiSelectOption[]> => {
    const query = typeahead.trim();
    const paging =
      query === '' && options?.browseOnEmpty ? availableCoachesBrowsePagesModifier : typeaheadPagesModifier;
    const modifier = {
      ...paging,
      sortBy: options?.sortBy,
      filters: {
        ...(query === '' ? {} : { query }),
        ...(options?.filters || {}),
      },
    } satisfies GetCoachesModifier;

    return queryClient
      .fetchQuery({
        ...getCoaches(modifier),
        staleTime: 30000,
        queryKey: ['AVAILABLE_COACHES', modifier],
      })
      .then((results) => {
        const coaches = options?.sortByAvailabilityPriority
          ? sortAvailableCoachesByPriority(results.coaches)
          : results.coaches;

        if (options?.shouldDisplayOverallGrade) {
          return coaches.map(buildCoachSelectOptions);
        }

        return coaches.map(({ id, fullNameOrEmail }) => buildUserSelectOptions({ id, fullNameOrEmail } as UserLike));
      });
  };
};

const availableCoachesDefaultSortBy = ['overallGrade-asc'] satisfies GetCoachesSortOrder;
const availableCoachesBrowsePagesModifier = { pageIndex: 0, pageSize: 50 } satisfies PagingModifier;

export const studentsTypeaheadSearch = (
  queryClient: QueryClient,
  filtersWithoutTypeahead?: Omit<GetUsersTypeaheadFilter, 'typeahead'>,
) => {
  return userTypeaheadSearch(queryClient, { roles: [student().role], ...filtersWithoutTypeahead });
};

export function useRequiredOnboardingLessonsData(lessonName: string, learningPathName: string) {
  const { learningPaths = EmptyArray, isPending: isLoading, error } = useAllLearningPaths();
  useErrorToast(error, 'Error fetching learning paths/lessons when generating onboarding sessions');
  const learningPath = learningPaths.find((path) => path.name === learningPathName);
  const startingLesson = learningPath?.learningPathLessons.find(({ lesson }) => lesson.name === lessonName)?.lesson;
  const missingPredefinedLessons = !startingLesson || !learningPath;

  return {
    startingLesson,
    learningPath,
    missingPredefinedLessons,
    isLoading,
  };
}

export const useAvailableCoachesSearch = (
  modifier?: GetCoachesModifier,
  options?: CoachesTypeaheadSearchOptions,
  fallbackCoach?: FullCoach | null,
) => {
  return useQuery({
    queryKey: ['AVAILABLE_COACHES', modifier, fallbackCoach?.id],
    queryFn: async () => {
      const coachesQuery = getCoaches({
        ...availableCoachesBrowsePagesModifier,
        ...modifier,
        sortBy: modifier?.sortBy ?? availableCoachesDefaultSortBy,
      });
      const results = await coachesQuery.queryFn();

      const fetched =
        fallbackCoach && !results.coaches.some((c) => c.id === fallbackCoach.id)
          ? [...results.coaches, fallbackCoach]
          : results.coaches;

      const coaches = options?.sortByAvailabilityPriority ? sortAvailableCoachesByPriority(fetched) : fetched;

      if (options?.shouldDisplayOverallGrade) {
        return coaches.map(buildCoachSelectOptions);
      }

      return coaches.map(({ id, fullNameOrEmail }) => buildUserSelectOptions({ id, fullNameOrEmail } as UserLike));
    },
    staleTime: 30000,
    enabled: !!modifier,
  });
};

/**
 * Custom hook for fetching coach data by ID
 * Returns a function that fetches coach data and normalizes the response
 */
export function useCoachById() {
  const queryClient = useQueryClient();

  const fetchCoachById = useCallback(
    async (coachId: string) => {
      const queryConfig = getCoach(coachId);
      const result = (await queryClient.fetchQuery(queryConfig)) as { fullNameOrEmail: string; id: string };
      return { name: result.fullNameOrEmail, id: result.id };
    },
    [queryClient],
  );

  return fetchCoachById;
}

/**
 * Custom hook for fetching lesson data by ID
 * Returns a function that fetches lesson data and normalizes the response
 */
export function useLessonById() {
  const queryClient = useQueryClient();

  const fetchLessonById = useCallback(
    async (lessonId: string) => {
      const queryConfig = getLesson(lessonId);
      const result = (await queryClient.fetchQuery(queryConfig)) as { name: string; id: string };
      return { name: result.name, id: result.id };
    },
    [queryClient],
  );

  return fetchLessonById;
}

export function usePayCycleOptions(): MultiSelectOption[] {
  const { data: payCycleOptions, isPending } = useQuery(listPayCycles());

  const formatOptions = (payCycle: GetPayCyclesResult): MultiSelectOption => {
    const { startPlainDate, endPlainDate } = payCycle;

    const startDate = startPlainDate.toLocaleString('en-US', { month: 'short' });
    const endDate = endPlainDate.toLocaleString('en-US', { month: 'short' });

    return {
      label: `${startDate} ${startPlainDate.day} - ${endDate} ${endPlainDate.day}, ${endPlainDate.year}`,
      value: `${startPlainDate}/${endPlainDate}`,
    };
  };

  return useMemo(() => (isPending ? [] : payCycleOptions?.map(formatOptions) ?? []), [payCycleOptions, isPending]);
}
